/* ============================================================================
 * approvalsData.js — K.O. Command approval inbox (Phase 5).
 *
 * Reads/decides `kc_approvals`: the rows Milli (the K.O. Command
 * orchestrator) creates when a tool call needs a human decision before it
 * proceeds — anything that leaves the building, costs money, or is legally
 * binding (AGENT_CONSTITUTION.md; migrations 043/044).
 *
 * Access model, matching 043 exactly:
 *   - Reads go straight through supabase-js SELECT; RLS permits them only
 *     for admins (`kc_approvals_admin_select`), so no RPC is needed and a
 *     crew_lead session gets zero rows, not an error.
 *   - Decisions go ONLY through the `kc_approval_decide` RPC, which
 *     re-checks admin, requires decided_by = auth.uid(), permits only
 *     pending rows, and writes an immutable kc_events audit row. There is
 *     deliberately no update path from the client.
 * ========================================================================== */

import { supabase } from '@/lib/supabaseClient';

/** Statuses `kc_approval_decide` accepts from this page. ('modified' and
 * 'expired' exist in the schema but aren't offered here — modified implies
 * an alternative-editing flow this page doesn't have yet, and expiry is a
 * deadline's job, not a button's.) */
export const DECISIONS = ['approved', 'rejected'];

export const fetchApprovals = async () => {
  const { data, error } = await supabase
    .from('kc_approvals')
    .select(
      'id,decision_needed,business,department,recommended_action,reason,' +
        'exposure,deadline,alternatives,no_action_consequence,status,' +
        'decided_at,created_at'
    )
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

/** One approval by id, pending or decided; null when there is none (or the
 * caller is not the owner, since RLS returns no row). */
export const fetchApproval = async (id) => {
  if (!id) return null;
  const { data, error } = await supabase.from('kc_approvals').select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return data ?? null;
};

export const decideApproval = async (approvalId, newStatus) => {
  if (!DECISIONS.includes(newStatus)) {
    throw new Error(`Unsupported decision: ${newStatus}`);
  }
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) throw userError;
  const uid = userData?.user?.id;
  if (!uid) throw new Error('Not signed in.');

  const { error } = await supabase.rpc('kc_approval_decide', {
    approval_id: approvalId,
    new_status: newStatus,
    decided_by: uid,
  });
  if (error) throw error;
};

/** Edit-then-approve (migration 072): approve with J.O.'s edits. The RPC
 * applies them to the row behind the card, lands the approval as
 * 'modified', audits the before/after, and leaves Milli a memory note.
 * `edits` uses the server's field names (caption; subject, body; title,
 * body, cta_url). */
export const decideApprovalModified = async (approvalId, edits) => {
  if (!edits || typeof edits !== 'object' || !Object.keys(edits).length) {
    throw new Error('No edits to apply.');
  }
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) throw userError;
  const uid = userData?.user?.id;
  if (!uid) throw new Error('Not signed in.');

  const { data, error } = await supabase.rpc('kc_approval_decide_modified', {
    approval_id: approvalId,
    decided_by: uid,
    p_edits: edits,
  });
  if (error) throw error;
  return data;
};

export const splitApprovals = (rows = []) => ({
  pending: rows.filter((r) => r?.status === 'pending'),
  decided: rows.filter((r) => r?.status !== 'pending'),
});

/** Design previews (migration 069). A design request's approval card carries
 * the generated versions; this returns { [approvalId]: [{ path, variant,
 * url, width, height }] } with short-lived signed URLs from the private
 * kc-design bucket. The RPC and the bucket policy are both owner-gated, so a
 * non-owner simply gets an empty map. Never throws: a card with no preview
 * is still a decidable card. */
export const fetchDesignPreviews = async (approvalIds = []) => {
  const ids = approvalIds.filter(Boolean);
  if (!ids.length) return {};
  try {
    const { data, error } = await supabase.rpc('kc_design_previews', {
      p_approval_ids: ids,
    });
    if (error) throw error;
    const rows = Array.isArray(data) ? data : [];
    const paths = rows.flatMap((r) =>
      (Array.isArray(r.assets) ? r.assets : []).map((a) => a.path).filter(Boolean)
    );
    if (!paths.length) return {};
    const { data: signed, error: signErr } = await supabase.storage
      .from('kc-design')
      .createSignedUrls(paths, 60 * 60);
    if (signErr) throw signErr;
    const urlByPath = Object.fromEntries(
      (signed || []).filter((s) => s?.signedUrl).map((s) => [s.path, s.signedUrl])
    );
    const out = {};
    for (const r of rows) {
      out[r.approval_id] = (Array.isArray(r.assets) ? r.assets : [])
        .filter((a) => a?.path && urlByPath[a.path])
        .map((a) => ({
          path: a.path,
          variant: a.variant,
          width: a.width,
          height: a.height,
          url: urlByPath[a.path],
        }));
    }
    return out;
  } catch {
    return {};
  }
};

/** What the card is actually approving, drawn later by ApprovalPreview.
 * Looks behind each pending approval for the row that carries the real
 * thing: a social post (070), an outbound email (057), a batch content
 * item (062/064, blog posts and captions), or a video (076). Returns
 * { [approvalId]: { kind: 'social'|'email'|'article'|'content'|'video', ... } }.
 * Every table is owner-gated by RLS, so a non-owner gets an empty map, and a
 * private-bucket file becomes a one-hour signed URL. Never throws: the card
 * is still decidable without its preview. */
export const fetchPostPreviews = async (approvalIds = []) => {
  const ids = approvalIds.filter(Boolean);
  if (!ids.length) return {};
  const out = {};
  const safe = async (fn) => {
    try {
      await fn();
    } catch {
      /* one missing preview must not blank the others */
    }
  };

  await Promise.all([
    safe(async () => {
      // Video (076): the rendered MP4 and its poster frame, signed for an hour.
      const { data, error } = await supabase.rpc('kc_video_previews', { p_approval_ids: ids });
      if (error) throw error;
      const rows = Array.isArray(data) ? data : [];
      const paths = rows.flatMap((r) =>
        (Array.isArray(r.assets) ? r.assets : []).flatMap((a) => [a.path, a.poster_path]).filter(Boolean)
      );
      let urlByPath = {};
      if (paths.length) {
        const { data: signed } = await supabase.storage
          .from('kc-design')
          .createSignedUrls(paths, 60 * 60);
        urlByPath = Object.fromEntries(
          (signed || []).filter((s) => s?.signedUrl).map((s) => [s.path, s.signedUrl])
        );
      }
      for (const r of rows) {
        const a = (Array.isArray(r.assets) ? r.assets : [])[0];
        if (!a?.path || !urlByPath[a.path]) continue;
        out[r.approval_id] = {
          kind: 'video',
          business: r.business,
          purpose: r.purpose,
          aspect: r.aspect,
          videoUrl: urlByPath[a.path],
          posterUrl: a.poster_path ? urlByPath[a.poster_path] || null : null,
          width: a.width,
          height: a.height,
          duration: a.duration,
          scenes: Array.isArray(r.scenes) ? r.scenes : [],
        };
      }
    }),
    safe(async () => {
      const { data, error } = await supabase
        .from('kc_social_posts')
        .select('approval_id,business,channels,caption,link_url,image_url,image_path')
        .in('approval_id', ids);
      if (error) throw error;
      const rows = data || [];
      const paths = rows.map((r) => r.image_path).filter(Boolean);
      let urlByPath = {};
      if (paths.length) {
        const { data: signed } = await supabase.storage
          .from('kc-design')
          .createSignedUrls(paths, 60 * 60);
        urlByPath = Object.fromEntries(
          (signed || []).filter((s) => s?.signedUrl).map((s) => [s.path, s.signedUrl])
        );
      }
      for (const r of rows) {
        out[r.approval_id] = {
          kind: 'social',
          business: r.business,
          channels: Array.isArray(r.channels) ? r.channels : [],
          caption: r.caption,
          linkUrl: r.link_url,
          imageUrl: r.image_url || (r.image_path ? urlByPath[r.image_path] : null) || null,
        };
      }
    }),
    safe(async () => {
      const { data, error } = await supabase
        .from('kc_outbound_emails')
        .select('approval_id,to_email,subject,body,business')
        .in('approval_id', ids);
      if (error) throw error;
      for (const r of data || []) {
        out[r.approval_id] = {
          kind: 'email',
          to: r.to_email,
          subject: r.subject,
          body: r.body,
          business: r.business || null,
        };
      }
      // FormQuick support replies (096): show the customer's message above
      // Milli's answer, and the class the brake table uses.
      const { data: support } = await supabase
        .from('kc_support_replies')
        .select('approval_id,class,auto_sent,inbound:kc_inbound_emails(from_name,subject,body_text,received_at)')
        .in('approval_id', ids);
      for (const s of support || []) {
        if (!out[s.approval_id]) continue;
        out[s.approval_id].support = {
          class: s.class,
          autoSent: !!s.auto_sent,
          fromName: s.inbound?.from_name || null,
          subject: s.inbound?.subject || null,
          body: s.inbound?.body_text || null,
          receivedAt: s.inbound?.received_at || null,
        };
      }
    }),
    safe(async () => {
      const { data, error } = await supabase
        .from('kc_content_items')
        .select('approval_id,kind,title,body,variants,cta_url,publish_date,business')
        .in('approval_id', ids);
      if (error) throw error;
      for (const r of data || []) {
        if (out[r.approval_id]) continue;
        const isBlog = /blog|article/i.test(String(r.kind || ''));
        out[r.approval_id] = isBlog
          ? {
              kind: 'article',
              title: r.title,
              body: r.body,
              ctaUrl: r.cta_url,
              publishDate: r.publish_date,
              business: r.business,
            }
          : {
              // A batch post: the exact Facebook and Instagram text that
              // publishes (the internal title never goes out).
              kind: 'content',
              facebook: (r.variants && r.variants.facebook) || r.body || '',
              instagram: (r.variants && r.variants.instagram) || r.body || '',
            };
      }
    }),
  ]);
  return out;
};

/** A pending approval whose deadline has passed. Still decidable — the DB
 * doesn't auto-expire — but the page should make the lateness loud. */
export const isPastDeadline = (row, now = new Date()) =>
  row?.status === 'pending' &&
  !!row?.deadline &&
  new Date(row.deadline).getTime() < now.getTime();

/** `alternatives` is a jsonb array whose entries Milli writes as either
 * plain strings or {option, tradeoff}-shaped objects. Normalize both to
 * display strings and drop anything unrenderable. */
export const normalizeAlternatives = (alternatives) => {
  if (!Array.isArray(alternatives)) return [];
  return alternatives
    .map((alt) => {
      if (typeof alt === 'string') return alt.trim();
      if (alt && typeof alt === 'object') {
        const option = String(alt.option || alt.action || '').trim();
        const tradeoff = String(alt.tradeoff || alt.consequence || '').trim();
        if (option && tradeoff) return `${option} — ${tradeoff}`;
        return option || tradeoff;
      }
      return '';
    })
    .filter(Boolean);
};
